/**
 * Tipos da Importação Inteligente — espelham o contrato de extração
 * (poc/schema-extracao.json, v0.1) e as colunas de documento_programa.
 */

export type TipoDocumento = "PCMSO" | "PGR";
/**
 * `substituido` = trocado automaticamente pela renovação do MESMO escopo.
 * `inativo` = desligado à mão (empresa unificou/repartiu os PCMSOs e os escopos
 * não se correspondem); sai de vigência, fica no histórico e pode ser reativado.
 */
export type StatusDocumento =
  | "rascunho"
  | "em_revisao"
  | "aprovado"
  | "substituido"
  | "inativo";
export type StatusExtracao = "pendente" | "processando" | "concluida" | "erro";
export type Confianca = "alta" | "media" | "baixa";
export type GrupoRisco =
  | "fisico"
  | "quimico"
  | "biologico"
  | "ergonomico"
  | "acidente"
  | "psicossocial";
/** Chave da aptidão no catálogo.
 *
 * Era uma união fechada, espelhando a lista do código. Com o catálogo virando
 * a fonte (a IA passa a extrair o que estiver cadastrado), fixar as chaves
 * aqui faria o front ignorar toda aptidão criada depois. Os rótulos vêm do
 * catálogo; APTIDAO_ROTULOS ficou só como fallback das originais. */
export type AptidaoChave = string;

// ---------- payload da extração (documento_programa.extracao_ia) ----------
export interface HistoricoRevisao {
  numero: string;
  data: string | null;
  descricao: string;
}

export interface ExtracaoDocumento {
  tipo: TipoDocumento;
  revisao: string | null;
  data_emissao: string | null;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
  elaborador: string | null;
  historico_revisoes?: HistoricoRevisao[];
}

export interface ExtracaoEmpresa {
  razao_social: string;
  cnpj: string;
  endereco?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cnae?: string | null;
  grau_risco?: number | null;
}

export interface MedicoCoordenador {
  nome: string;
  crm: string | null;
  uf_crm: string | null;
  rqe?: string | null;
  especialidade?: string | null;
}

export interface ExtracaoUnidade {
  nome: string;
  cnpj_local: string | null;
  contratante: string | null;
  endereco?: string | null;
  cidade: string | null;
  uf: string | null;
}

export interface Risco {
  grupo: GrupoRisco;
  perigo: string;
  agravos: string | null;
}

export interface Exigencia {
  exame: string;
  codigo_esocial: string | null;
  admissional: boolean;
  periodicidade_meses: number | null;
  retorno_trabalho: boolean;
  mudanca_risco: boolean;
  demissional: boolean;
  sexo: "M" | "F" | null;
  faixa_etaria: string | null;
  confianca: Confianca;
}

export interface FuncaoVinculada {
  unidade: string | null;
  setor: string | null;
  funcao: string;
}

export interface Ghe {
  codigo: string;
  nome: string | null;
  riscos: Risco[];
  aptidoes_especificas: AptidaoChave[];
  exigencias: Exigencia[];
  funcoes: FuncaoVinculada[];
}

export interface AvisoExtracao {
  campo: string;
  motivo: string;
  pagina: number | null;
}

export interface ExtracaoPayload {
  documento: ExtracaoDocumento;
  empresa: ExtracaoEmpresa;
  medico_coordenador: MedicoCoordenador | null;
  unidades: ExtracaoUnidade[];
  ghes: Ghe[];
  avisos_extracao: AvisoExtracao[];
}

// ---------- registro do documento (API) ----------
export interface DocumentoPrograma {
  id: number;
  empresa_id: number;
  tipo: TipoDocumento;
  /** Rótulo do contrato/lotação coberto pelo documento; "" = empresa toda. */
  escopo: string;
  revisao: string | null;
  data_emissao: string | null;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
  medico_coordenador_nome: string | null;
  medico_coordenador_crm: string | null;
  medico_coordenador_uf: string | null;
  elaborador: string | null;
  arquivo_blob: string;
  status: StatusDocumento;
  extracao_status: StatusExtracao;
  extracao_erro: string | null;
  aprovado_por: string | null;
  aprovado_em: string | null;
  substituido_por: number | null;
  criado_em: string;
}

export interface DocumentoDetalhe extends DocumentoPrograma {
  extracao_ia: ExtracaoPayload | null;
}

/** Escopo pra exibição — vazio significa que o documento vale pra empresa toda. */
export function rotuloEscopo(escopo: string | null | undefined): string {
  return (escopo ?? "").trim() || "empresa toda";
}

/** Fallback para as 5 originais — a fonte é o catálogo (GET /aptidoes). */
export const APTIDAO_ROTULOS: Record<string, string> = {
  trabalho_em_altura: "Trabalho em Altura",
  espaco_confinado: "Espaço Confinado",
  maquinas_moveis: "Operação de Máquinas Móveis",
  protecao_respiratoria: "Proteção Respiratória",
  trabalho_com_eletricidade: "Trabalho com Eletricidade",
};

export const GRUPOS_RISCO: GrupoRisco[] = [
  "fisico",
  "quimico",
  "biologico",
  "ergonomico",
  "acidente",
  "psicossocial",
];

export const GRUPO_RISCO_ROTULOS: Record<GrupoRisco, string> = {
  fisico: "Físico",
  quimico: "Químico",
  biologico: "Biológico",
  ergonomico: "Ergonômico",
  acidente: "Acidente",
  psicossocial: "Psicossocial",
};
